import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Check, X } from "lucide-react";

const tiers = [
  {
    id: "culture-recap",
    name: "Culture Recap",
    price: "From $495",
    unit: "/ quarter",
    sub: null,
    blurb: "For teams that want their all-hands to become something employees can return to.",
    features: [
      "A recap email",
      "Slack-ready highlights",
      "Decisions and next steps, with owners and due dates where you have supplied them",
      "A recognition shout-out",
      "A short values-in-action story",
      "A team pride moment",
      "One revision round",
    ],
    note: "One editable document with separate copy-ready sections. You review and approve the content before it is used.",
    magazine: "Magazine and extras quoted separately.",
    cta: "Request Pricing",
  },
  {
    id: "culture-pulse-partner",
    name: "Culture Pulse Partner",
    price: "From $995",
    unit: "/ quarter",
    sub: null,
    blurb: "For teams that want the recap plus a simple check-in and visible follow-through.",
    features: [
      "Everything in Culture Recap, plus:",
      "A five-question team check-in for up to 50 employees, using your own approved survey tool",
      "A short themes brief for leaders",
      "A 30-minute leader call",
      "One agreed action to focus on",
      "A \u201cYou said / We\u2019re doing\u201d update to close the loop",
    ],
    note: "The check-in runs through your own approved survey tool — privacy settings, access and reporting rules are agreed with you before anything is collected.",
    magazine: "Magazine and extras quoted separately.",
    cta: "Request Pricing",
  },
  {
    id: "one-off",
    name: "One-Off Recap",
    price: "$895",
    unit: "",
    sub: "One-time",
    blurb: "A one-time option — no annual commitment.",
    features: [
      "One all-hands meeting recap (up to 60 minutes; recording or transcript and any slides provided by you)",
      "Recap email, ready to send to your team",
      "Slack-ready highlights, ready to paste",
      "Decisions and next steps, with an owner and due date for each item",
      "One recognition shout-out",
      "One revision round",
      "Draft delivered within 2 business days of receiving complete materials",
      "You approve everything before it is shared",
    ],
    note: null,
    magazine: null,
    notIncludedLine:
      "Not included: employee surveys or check-ins, leader calls, magazine, sending on your behalf, legal or HR review. Magazine and extras are quoted separately.",
    cta: "Request a Sample Recap",
  },
];

const included = [
  "Drafted recap email",
  "Slack-ready highlights",
  "Decisions and next steps list",
  "Revisions as stated in your plan",
];

const notIncluded = [
  "Sending messages on your behalf",
  "Legal or HR review of your content",
  "Anything not in your materials",
];

export const Pricing = ({ openLead }) => (
  <section id="pricing" data-testid="pricing-section" className="py-28 lg:py-36">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta">
          Pricing
        </p>
        <h2 className="mt-5 max-w-2xl font-serif text-4xl font-medium leading-tight tracking-tight text-walnut sm:text-5xl">
          Cheaper than one more offsite.
        </h2>
      </Reveal>

      <div className="mt-20 grid items-stretch gap-8 lg:grid-cols-3">
        {tiers.map((tier, i) => (
          <motion.div
            key={tier.id}
            initial={{ opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.85, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`pricing-card-${tier.id}`}
            className="relative flex flex-col rounded-3xl border border-line bg-paper p-10 text-walnut shadow-[0_10px_30px_rgba(42,32,28,0.06)]"
          >
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-clay">
              {tier.name}
            </p>
            <p className="mt-4 text-sm leading-relaxed text-umber/85">{tier.blurb}</p>
            <p className="mt-6 flex items-baseline gap-2">
              <span className="font-serif text-3xl font-medium tracking-tight text-umber" data-testid={`price-${tier.id}`}>
                {tier.price}
              </span>
              {tier.unit && (
                <span className="font-mono text-sm text-clay">{tier.unit}</span>
              )}
            </p>
            {tier.sub && (
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-clay">
                {tier.sub}
              </p>
            )}
            <ul className="mt-9 flex-1 space-y-4">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                  <span className="text-umber/90">{f}</span>
                </li>
              ))}
            </ul>
            {tier.note && (
              <p className="mt-8 rounded-2xl border border-dashed border-walnut/25 bg-cream/60 px-5 py-4 text-sm leading-relaxed text-umber/85">
                {tier.note}
              </p>
            )}
            {tier.magazine && (
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">
                {tier.magazine}
              </p>
            )}
            {tier.notIncludedLine && (
              <p className="mt-6 text-xs leading-relaxed text-clay" data-testid={`not-included-${tier.id}`}>
                {tier.notIncludedLine}
              </p>
            )}
            <button
              data-testid={`pricing-cta-${tier.id}`}
              onClick={openLead}
              className="mt-8 rounded-full border border-walnut/30 py-3.5 text-sm font-semibold text-walnut transition-colors duration-300 hover:bg-walnut hover:text-paper"
            >
              {tier.cta}
            </button>
          </motion.div>
        ))}
      </div>

      <Reveal delay={0.1}>
        <p className="mt-12 text-center">
          <button
            data-testid="annual-plans-button"
            onClick={openLead}
            className="font-mono text-xs uppercase tracking-[0.25em] text-umber underline underline-offset-4 transition-colors duration-300 hover:text-terracotta"
          >
            Ask about annual plans.
          </button>
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div data-testid="included-block" className="mt-16 grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-line bg-paper p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-olive">
              What&rsquo;s included
            </p>
            <ul className="mt-5 space-y-3">
              {included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-umber/90">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-olive" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-line bg-paper p-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta">
              Not included
            </p>
            <ul className="mt-5 space-y-3">
              {notIncluded.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-umber/90">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
